import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type {
  Client,
  DeliveryMode,
  Distributor,
  Product,
  Sale,
  SaleOrigin,
  SaleType,
} from "@/types";
import { formatCOP, uid } from "@/utils/format";

export interface SaleFormOutput {
  sale: Sale;
  source: "casa" | "clear" | string; // string = id de asesor
  newClient?: Client;
}

interface SaleFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (out: SaleFormOutput) => void;
  products: Product[];
  clients: Client[];
  distributors: Distributor[];
}

const SALE_TYPES: SaleType[] = ["Full", "Consignación", "A Costo"];
const ORIGINS: SaleOrigin[] = ["Facebook", "Google", "TikTok", "Casa", "Recompra"];
const DELIVERIES: DeliveryMode[] = ["recojo", "domicilio"];

interface FormState {
  clientMode: "existente" | "nuevo";
  clientId: string;
  newName: string;
  newCedula: string;
  newPhone: string;
  newDepartment: string;
  newCity: string;
  newNeighborhood: string;
  newAddress: string;
  productId: string;
  qty: number;
  advisorId: string;
  type: SaleType;
  origin: SaleOrigin;
  delivery: DeliveryMode;
  source: string;
}

const SaleFormModal = ({
  open,
  onClose,
  onSubmit,
  products,
  clients,
  distributors,
}: SaleFormModalProps) => {
  const [form, setForm] = useState<FormState>({
    clientMode: "existente",
    clientId: "",
    newName: "",
    newCedula: "",
    newPhone: "",
    newDepartment: "",
    newCity: "",
    newNeighborhood: "",
    newAddress: "",
    productId: "",
    qty: 1,
    advisorId: "admin",
    type: "Full",
    origin: "Casa",
    delivery: "domicilio",
    source: "casa",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm({
      clientMode: clients.length > 0 ? "existente" : "nuevo",
      clientId: clients[0]?.id ?? "",
      newName: "",
      newCedula: "",
      newPhone: "",
      newDepartment: "",
      newCity: "",
      newNeighborhood: "",
      newAddress: "",
      productId: products[0]?.id ?? "",
      qty: 1,
      advisorId: "admin",
      type: "Full",
      origin: "Casa",
      delivery: "domicilio",
      source: "casa",
    });
  }, [open, clients, products]);

  const set = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const product = useMemo(
    () => products.find((p) => p.id === form.productId) ?? null,
    [products, form.productId]
  );
  const advisor = distributors.find((d) => d.id === form.advisorId);
  const marginPct = advisor ? advisor.margin : 25;
  const qty = Number(form.qty) || 0;
  const pv = product ? qty * product.pv : 0;
  const total = product ? qty * product.pricePublic : 0;

  const submit = () => {
    if (!product) {
      setError("Selecciona un producto del catálogo.");
      return;
    }
    if (qty <= 0) {
      setError("La cantidad debe ser mayor a cero.");
      return;
    }

    let clientId = form.clientId;
    let clientName = "";
    let newClient: Client | undefined;

    if (form.clientMode === "existente") {
      const c = clients.find((x) => x.id === form.clientId);
      if (!c) {
        setError("Selecciona un cliente.");
        return;
      }
      clientId = c.id;
      clientName = c.name;
    } else {
      if (!form.newName.trim()) {
        setError("Escribe el nombre del nuevo cliente.");
        return;
      }
      newClient = {
        id: uid("cli"),
        name: form.newName.trim(),
        cedula: form.newCedula.trim(),
        phone: form.newPhone.trim(),
        department: form.newDepartment.trim(),
        city: form.newCity.trim(),
        neighborhood: form.newNeighborhood.trim(),
        address: form.newAddress.trim(),
      };
      clientId = newClient.id;
      clientName = newClient.name;
    }

    const sale: Sale = {
      id: uid("sale"),
      date: new Date().toISOString(),
      clientId,
      clientName,
      productId: product.id,
      productName: product.name,
      qty,
      pv,
      total,
      marginPct,
      advisorId: form.advisorId,
      type: form.type,
      origin: form.origin,
      delivery: form.delivery,
    };

    onSubmit({ sale, source: form.source, newClient });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Registrar nueva venta"
      icon="ri-shopping-bag-3-line"
      size="lg"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} className={primaryButtonClass}>
            <i className="ri-save-3-line" /> Registrar venta
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Cliente */}
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between gap-2 mb-1">
            <label className={labelClass}>Cliente</label>
            <div className="flex items-center gap-1 bg-background-200/50 rounded-full p-1">
              {(["existente", "nuevo"] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => set({ clientMode: m })}
                  className={`px-3 py-1 rounded-full text-[11px] font-semibold cursor-pointer transition-colors ${
                    form.clientMode === m
                      ? "bg-accent-500/25 text-accent-200"
                      : "text-foreground-600 hover:text-accent-200"
                  }`}
                >
                  {m === "existente" ? "Existente" : "Nuevo"}
                </button>
              ))}
            </div>
          </div>

          {form.clientMode === "existente" ? (
            <select
              value={form.clientId}
              onChange={(e) => set({ clientId: e.target.value })}
              className={inputClass}
            >
              {clients.length === 0 && <option value="">Sin clientes registrados</option>}
              {clients.map((c) => (
                <option key={c.id} value={c.id} className="bg-background-100">
                  {c.name} {c.cedula ? `· ${c.cedula}` : ""}
                </option>
              ))}
            </select>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                value={form.newName}
                onChange={(e) => set({ newName: e.target.value })}
                placeholder="Nombre completo"
                className={inputClass}
              />
              <input
                value={form.newCedula}
                onChange={(e) => set({ newCedula: e.target.value })}
                placeholder="Cédula"
                className={inputClass}
              />
              <input
                value={form.newPhone}
                onChange={(e) => set({ newPhone: e.target.value })}
                placeholder="Celular"
                className={inputClass}
              />
              <input
                value={form.newDepartment}
                onChange={(e) => set({ newDepartment: e.target.value })}
                placeholder="Departamento (DTO)"
                className={inputClass}
              />
              <input
                value={form.newCity}
                onChange={(e) => set({ newCity: e.target.value })}
                placeholder="Ciudad / Municipio"
                className={inputClass}
              />
              <input
                value={form.newNeighborhood}
                onChange={(e) => set({ newNeighborhood: e.target.value })}
                placeholder="Barrio"
                className={inputClass}
              />
              <input
                value={form.newAddress}
                onChange={(e) => set({ newAddress: e.target.value })}
                placeholder="Agregar indicaciones (torre, manzana, casa...)"
                className={`${inputClass} sm:col-span-2`}
              />
            </div>
          )}
        </div>

        {/* Producto y cantidad */}
        <div className="sm:col-span-2">
          <label className={labelClass}>Producto del catálogo</label>
          <select
            value={form.productId}
            onChange={(e) => set({ productId: e.target.value })}
            className={inputClass}
          >
            {products.length === 0 && <option value="">Sin productos en catálogo</option>}
            {products.map((p) => (
              <option key={p.id} value={p.id} className="bg-background-100">
                {p.sku} · {p.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Cantidad</label>
          <input
            type="number"
            min={1}
            value={form.qty}
            onChange={(e) => set({ qty: Number(e.target.value) || 0 })}
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>Asesor responsable</label>
          <select
            value={form.advisorId}
            onChange={(e) => set({ advisorId: e.target.value })}
            className={inputClass}
          >
            <option value="admin" className="bg-background-100">
              Juanka (Admin)
            </option>
            {distributors.map((d) => (
              <option key={d.id} value={d.id} className="bg-background-100">
                {d.names} {d.surnames} · {d.margin}%
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Tipo de venta</label>
          <select
            value={form.type}
            onChange={(e) => set({ type: e.target.value as SaleType })}
            className={inputClass}
          >
            {SALE_TYPES.map((t) => (
              <option key={t} value={t} className="bg-background-100">
                {t}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Origen de captación</label>
          <select
            value={form.origin}
            onChange={(e) => set({ origin: e.target.value as SaleOrigin })}
            className={inputClass}
          >
            {ORIGINS.map((o) => (
              <option key={o} value={o} className="bg-background-100">
                {o}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Entrega</label>
          <select
            value={form.delivery}
            onChange={(e) => set({ delivery: e.target.value as DeliveryMode })}
            className={inputClass}
          >
            {DELIVERIES.map((d) => (
              <option key={d} value={d} className="bg-background-100">
                {d === "recojo" ? "Recojo en punto" : "Domicilio"}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass}>Descontar stock de</label>
          <select
            value={form.source}
            onChange={(e) => set({ source: e.target.value })}
            className={inputClass}
          >
            <option value="casa" className="bg-background-100">
              Casa / Oficina
            </option>
            <option value="clear" className="bg-background-100">
              Punto Clear
            </option>
            {distributors.map((d) => (
              <option key={d.id} value={d.id} className="bg-background-100">
                Consignación · {d.names}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500">PV de la venta</p>
          <p className="text-sm font-bold text-accent-200">{pv}</p>
        </div>
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500">Margen del asesor</p>
          <p className="text-sm font-bold text-foreground-950">{marginPct}%</p>
        </div>
        <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
          <p className="text-[10px] text-foreground-500">Total a cobrar</p>
          <p className="text-sm font-bold text-foreground-950">{formatCOP(total)}</p>
        </div>
      </div>

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default SaleFormModal;